import { AppConfig } from '@/config';
import type { User } from '@/types/User';
import { isValidAppyId } from '@/utils/isValidAppyId';
import axios from 'axios';
import { useStore } from '../Store';
import { StoreMap } from '../StoreMap';
import type { StoreSubscription } from '../StoreSubscription';

const LOCAL_STORAGE_KEY = 'currentUser';

export class UserProfileStore extends StoreMap<{ accountId: string; userId: string }, User> {
  private currentKey: { accountId: string; userId: string } | null = null;

  constructor() {
    super(24 * 60 * 60 * 1000); // 1 day
  }

  async Fetch(key: { accountId: string; userId: string }): Promise<User> {
    const { accountId, userId } = key;
    const url = `${AppConfig.apiHost}/v1/${AppConfig.appId}/accounts/${accountId}/${userId}`;
    const res = await axios.get<{ data: { attributes: User } }>(url);
    return res.data.data.attributes;
  }

  /** Set the current user and persist the key */
  setCurrentUserKey(key: { accountId: string; userId: string }): void {
    this.currentKey = key;

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(key));
    } catch {
      console.warn('LocalStorage unavailable; session is in-memory only');
    }
  }

  /** Read key from localStorage */
  getCurrentUserKey(): { accountId: string; userId: string } | null {
    if (this.currentKey) return this.currentKey;

    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const { accountId, userId } = parsed ?? {};

      if (!isValidAppyId(accountId) || !isValidAppyId(userId)) {
        return null;
      }

      this.currentKey = { accountId, userId };
      return this.currentKey;
    } catch {
      return null;
    }
  }

  /** Clear the current user */
  clearCurrentUserKey(): void {
    this.currentKey = null;

    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // ignore, storage might be disabled
    }
  }

  /** Returns true if a valid current user key exists */
  async isLoggedOn(): Promise<boolean> {
    const key = this.getCurrentUserKey();
    if (!key) return false;

    const user = await this.Get(key); // triggers fetch if not cached
    return user !== null;
  }

  /** Fetch the current user from store */
  async getCurrentUser(): Promise<User | null> {
    const key = this.getCurrentUserKey();
    if (!key) return null;
    return await this.Get(key);
  }

  /** Optionally expose current user as a ref */
  SubscribeCurrent(): StoreSubscription<User> | null {
    const key = this.getCurrentUserKey();
    return key ? this.Subscribe(key) : null;
  }

  HydrateFromLocalStorage(): { accountId: string; userId: string } | null {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const { accountId, userId } = parsed ?? {};

      if (!accountId || !userId || !isValidAppyId(accountId) || !isValidAppyId(userId)) {
        return null;
      }

      this.currentKey = { accountId, userId };
      return this.currentKey;
    } catch {
      return null;
    }
  }

  get hasSession(): boolean {
    return this.getCurrentUserKey() !== null;
  }
}

export function useUserProfileStore(): UserProfileStore {
  return useStore(UserProfileStore);
}
